import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { aiMessagingAPI, dailyNudgeAPI, adminAPI } from '../../services/api';

/**
 * Messaging Hub — merges "AI & Contextual Messaging" (re-engagement copy engine)
 * and the "Daily Food Nudge" broadcast into ONE page with a shared status
 * dashboard + two tabs. Both share the same brand voice / Anthropic model; the
 * nudge keeps its own length/genre + send times + craving photos.
 */

const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' });
const CAMPAIGNS = [
  { v: 'winback', l: 'Win-back (lapsed)' },
  { v: 'first_order', l: 'First-order (new)' },
  { v: 'browse_no_order', l: 'Browsed, no order' },
];
const DAYPARTS = ['morning', 'lunch', 'evening', 'dinner', 'late_night'];
const WEATHERS = ['unknown', 'clear', 'rain', 'hot', 'cold'];
const emptyRule = () => ({ name: '', enabled: true, dayparts: [], dayType: 'any', weather: 'any', message: '', couponCode: '', category: '', priority: 0 });

// ---- shared bits ------------------------------------------------------------

const Chip = ({ label, value, color, sub }) => (
  <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '10px 14px', minWidth: 150, flex: '1 1 150px', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: color || '#111', marginTop: 2 }}>{value}</div>
    {sub ? <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{sub}</div> : null}
  </div>
);

// Next upcoming "HH:MM" (IST) from a list of times; shows how it reads to a human.
function nextSendLabel(times) {
  if (!Array.isArray(times) || !times.length) return '—';
  const sorted = [...times].filter(Boolean).sort();
  return sorted.join(' · ');
}

// ======================= AI COPY & CONTEXT TAB ===============================
function AiCopyTab({ onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [weatherConfigured, setWeatherConfigured] = useState(false);
  const [context, setContext] = useState(null);
  const [genreOptions, setGenreOptions] = useState(['witty', 'desi_comedy', 'emotional', 'foodgasm', 'filmy', 'flirty', 'warm', 'playful', 'urgent', 'foodie', 'minimal', 'bold']);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [pv, setPv] = useState({ campaign: 'winback', stage: 'reminder', segmentLabel: 'lapsed customer', offer: '', sampleName: 'Aarav', favItem: 'Paneer Butter Masala', favRestaurant: 'Curry Heaven' });
  const [pvResult, setPvResult] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);

  const [variants, setVariants] = useState(null);
  const [vLoading, setVLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await aiMessagingAPI.getSettings();
      const d = r.data.data || {};
      setAiConfigured(!!d.aiConfigured);
      setWeatherConfigured(!!d.weatherConfigured);
      setContext(d.currentContext || null);
      setGenreOptions(d.genreOptions || ['witty', 'desi_comedy', 'emotional', 'foodgasm', 'filmy', 'flirty', 'warm', 'playful', 'urgent', 'foodie', 'minimal', 'bold']);
      setCfg({
        enabled: !!d.enabled,
        brandVoice: d.brandVoice || '',
        model: d.model || '',
        temperature: typeof d.temperature === 'number' ? d.temperature : 0.7,
        length: d.length || 'short',
        genre: d.genre || '',
        language: d.language || 'hinglish',
        useForCampaigns: d.useForCampaigns || [],
        maxCohortGenerationsPerRun: d.maxCohortGenerationsPerRun ?? 20,
        context: {
          mode: (d.context && d.context.mode) || 'manual',
          defaultCity: (d.context && d.context.defaultCity) || '',
          weather: (d.context && d.context.weather) || 'unknown',
        },
        contextualRules: (d.contextualRules || []).map(r => ({ ...emptyRule(), ...r })),
      });
    } catch (e) { setMsg('Failed to load settings'); }
  }, []);
  const loadVariants = useCallback(async () => {
    setVLoading(true);
    try { const r = await aiMessagingAPI.getVariants(); setVariants(r.data.data.variants || []); }
    catch (e) { /* non-fatal */ }
    finally { setVLoading(false); }
  }, []);
  useEffect(() => { load(); loadVariants(); }, [load, loadVariants]);

  const save = async () => {
    setSaving(true); setMsg('');
    try { await aiMessagingAPI.updateSettings(cfg); setMsg('Saved.'); load(); onChanged && onChanged(); }
    catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));
  const setCtx = (patch) => setCfg(prev => ({ ...prev, context: { ...prev.context, ...patch } }));
  const refreshContext = async () => {
    try { const r = await aiMessagingAPI.getContext(); setContext(r.data.data.context); setMsg('Weather/context refreshed.'); }
    catch (e) { setMsg('Failed to fetch weather/context'); }
  };
  const toggleCampaign = (v) => set('useForCampaigns', cfg.useForCampaigns.includes(v) ? cfg.useForCampaigns.filter(c => c !== v) : [...cfg.useForCampaigns, v]);
  const setRule = (i, k, v) => set('contextualRules', cfg.contextualRules.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const toggleRuleDaypart = (i, dp) => { const r = cfg.contextualRules[i]; setRule(i, 'dayparts', r.dayparts.includes(dp) ? r.dayparts.filter(d => d !== dp) : [...r.dayparts, dp]); };
  const runPreview = async () => {
    setPvLoading(true); setPvResult(null);
    try { const r = await aiMessagingAPI.preview({ ...pv, length: cfg.length, genre: cfg.genre, language: cfg.language }); setPvResult(r.data.data); }
    catch (e) { setMsg(e.response?.data?.message || 'Preview failed'); }
    finally { setPvLoading(false); }
  };

  if (!cfg) return <div style={{ padding: 16 }}>🔄 Loading…</div>;

  return (
    <div>
      {!aiConfigured && (
        <div style={{ ...card, borderColor: '#F59E0B', background: '#FFFBEB', color: '#92400E' }}>
          ⚠️ <b>ANTHROPIC_API_KEY is not set on the server.</b> AI copy is skipped (templates used) until it's configured. Everything else here still saves.
        </div>
      )}
      {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669' }}>{msg}</div>}

      <div style={card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={e => set('enabled', e.target.checked)} />
          <b>AI copy {cfg.enabled ? 'ON' : 'OFF'}</b>
          <span style={{ fontSize: 12, color: '#999' }}>{cfg.enabled ? 'Re-engagement pushes use AI-written copy.' : 'Off — templates only.'}</span>
        </label>
        {context && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#555' }}>
            Current context: <b>{context.daypart}</b> · <b>{context.dayType}</b> · weather <b>{context.weather}</b> (IST hour {context.hourIST})
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Brand voice <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>(shared — also drives the Daily Nudge copy)</span></h3>
        <textarea value={cfg.brandVoice} onChange={e => set('brandVoice', e.target.value)} rows={3} style={{ width: '100%', boxSizing: 'border-box' }} placeholder="How should the copy sound?" />
        <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>Length<br />
            <select value={cfg.length} onChange={e => set('length', e.target.value)}>
              <option value="short">Short (punchy)</option><option value="medium">Medium</option><option value="long">Long (descriptive)</option>
            </select>
          </label>
          <label>Language<br />
            <select value={cfg.language} onChange={e => set('language', e.target.value)}>
              <option value="hinglish">Hinglish (Hindi + English) — recommended</option>
              <option value="hindi">Hindi (Devanagari)</option>
              <option value="english">English (casual Indian)</option>
            </select>
          </label>
          <label>Genre / tone <span style={{ fontSize: 11, color: '#999' }}>(pick or type custom)</span><br />
            <input list="ai-genres" value={cfg.genre} onChange={e => set('genre', e.target.value)} placeholder="e.g. witty" style={{ width: 200 }} />
            <datalist id="ai-genres">{genreOptions.map(g => <option key={g} value={g} />)}</datalist>
          </label>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
          Tip: <strong>Hinglish</strong> + tone <strong>witty</strong> / <strong>desi_comedy</strong> / <strong>emotional</strong> gives the friendly, funny, Zomato-style copy. Use the live preview below to try it.
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
          <label>Model override (blank = server default)<br />
            <input value={cfg.model} onChange={e => set('model', e.target.value)} placeholder="claude-haiku-4-5-…" style={{ width: 240 }} />
          </label>
          <label>Temperature ({cfg.temperature})<br />
            <input type="range" min="0" max="1" step="0.1" value={cfg.temperature} onChange={e => set('temperature', Number(e.target.value))} />
          </label>
          <label>Max AI generations / run (cost cap)<br />
            <input type="number" min="0" max="200" value={cfg.maxCohortGenerationsPerRun} onChange={e => set('maxCohortGenerationsPerRun', Number(e.target.value))} style={{ width: 80 }} />
          </label>
        </div>
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 13, color: '#555' }}>Use AI copy for: </span>
          {CAMPAIGNS.map(c => (
            <label key={c.v} style={{ marginRight: 12, fontSize: 13 }}>
              <input type="checkbox" checked={cfg.useForCampaigns.includes(c.v)} onChange={() => toggleCampaign(c.v)} /> {c.l}
            </label>
          ))}
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Weather context</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label><input type="radio" name="wmode" checked={cfg.context.mode === 'manual'} onChange={() => setCtx({ mode: 'manual' })} /> Manual (I set it)</label>
          <label><input type="radio" name="wmode" checked={cfg.context.mode === 'auto'} onChange={() => setCtx({ mode: 'auto' })} /> Auto-fetch by city</label>
        </div>
        {cfg.context.mode === 'manual' ? (
          <div style={{ marginTop: 10 }}>
            <select value={cfg.context.weather} onChange={e => setCtx({ weather: e.target.value })} className="form-control" style={{ maxWidth: 200 }}>
              {WEATHERS.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <span style={{ fontSize: 12, color: '#999', marginLeft: 10 }}>Feeds the AI copy + contextual rules (e.g. rain → comfort food).</span>
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            {!weatherConfigured && (
              <div style={{ fontSize: 13, color: '#92400E', marginBottom: 8 }}>
                ⚠️ OPENWEATHERMAP_API_KEY is not set on the server — auto weather will fall back to the manual value below.
              </div>
            )}
            <label>City for auto weather (used by the re-engagement run):<br />
              <input value={cfg.context.defaultCity} onChange={e => setCtx({ defaultCity: e.target.value })} placeholder="e.g. Agra" style={{ width: 220 }} />
            </label>
            <button onClick={refreshContext} style={{ ...btn('#6366F1'), marginLeft: 10 }}>↻ Fetch now</button>
            <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
              Fetched from OpenWeatherMap (15-min cache), mapped to clear/rain/hot/cold. Fallback (on error / no key):{' '}
              <select value={cfg.context.weather} onChange={e => setCtx({ weather: e.target.value })} style={{ fontSize: 12 }}>
                {WEATHERS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Contextual rules <span style={{ fontSize: 12, color: '#999' }}>(promo angle woven into the copy when the context matches)</span></h3>
        {cfg.contextualRules.map((r, i) => (
          <div key={i} style={{ border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label><input type="checkbox" checked={r.enabled} onChange={e => setRule(i, 'enabled', e.target.checked)} /> on</label>
              <input value={r.name} onChange={e => setRule(i, 'name', e.target.value)} placeholder="Rule name" style={{ width: 160 }} />
              <select value={r.dayType} onChange={e => setRule(i, 'dayType', e.target.value)}>
                <option value="any">any day</option><option value="weekday">weekday</option><option value="weekend">weekend</option>
              </select>
              <select value={r.weather} onChange={e => setRule(i, 'weather', e.target.value)}>
                {['any', 'clear', 'rain', 'hot', 'cold'].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              <label>priority <input type="number" value={r.priority} onChange={e => setRule(i, 'priority', Number(e.target.value))} style={{ width: 56 }} /></label>
              <button onClick={() => set('contextualRules', cfg.contextualRules.filter((_, idx) => idx !== i))} style={{ ...btn('#EF4444'), padding: '4px 10px', marginLeft: 'auto' }}>Remove</button>
            </div>
            <div style={{ marginTop: 6 }}>
              <span style={{ fontSize: 12, color: '#666' }}>dayparts: </span>
              {DAYPARTS.map(dp => (
                <label key={dp} style={{ fontSize: 12, marginRight: 8 }}>
                  <input type="checkbox" checked={r.dayparts.includes(dp)} onChange={() => toggleRuleDaypart(i, dp)} /> {dp}
                </label>
              ))}
            </div>
            <input value={r.message} onChange={e => setRule(i, 'message', e.target.value)} placeholder="Promo angle / hint (e.g. 'hot soup & comfort food for the rain')" style={{ width: '100%', marginTop: 6, boxSizing: 'border-box' }} />
            <div style={{ marginTop: 6 }}>
              <input value={r.couponCode} onChange={e => setRule(i, 'couponCode', e.target.value)} placeholder="Coupon (optional)" style={{ width: 150, marginRight: 8 }} />
              <input value={r.category} onChange={e => setRule(i, 'category', e.target.value)} placeholder="Category (optional)" style={{ width: 150 }} />
            </div>
          </div>
        ))}
        <button onClick={() => set('contextualRules', [...cfg.contextualRules, emptyRule()])} style={btn('#6366F1')}>+ Add rule</button>
      </div>

      <button onClick={save} disabled={saving} style={{ ...btn('#10B981'), fontSize: 15 }}>{saving ? 'Saving…' : '💾 Save AI settings'}</button>

      <div style={{ ...card, marginTop: 24 }}>
        <h3 style={{ marginTop: 0 }}>🔍 Preview AI copy <span style={{ fontSize: 12, color: '#999' }}>(one live Anthropic call, uses the saved brand voice + current context)</span></h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>Campaign<br />
            <select value={pv.campaign} onChange={e => setPv({ ...pv, campaign: e.target.value })}>
              {CAMPAIGNS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </label>
          <label>Stage<br /><input value={pv.stage} onChange={e => setPv({ ...pv, stage: e.target.value })} style={{ width: 110 }} /></label>
          <label>Segment<br /><input value={pv.segmentLabel} onChange={e => setPv({ ...pv, segmentLabel: e.target.value })} style={{ width: 160 }} /></label>
          <label>Offer<br /><input value={pv.offer} onChange={e => setPv({ ...pv, offer: e.target.value })} placeholder="e.g. use SAVE20" style={{ width: 150 }} /></label>
          <label>Sample name<br /><input value={pv.sampleName} onChange={e => setPv({ ...pv, sampleName: e.target.value })} style={{ width: 100 }} /></label>
          <label>Fav dish<br /><input value={pv.favItem} onChange={e => setPv({ ...pv, favItem: e.target.value })} placeholder="(blank = generic)" style={{ width: 150 }} /></label>
          <label>Fav restaurant<br /><input value={pv.favRestaurant} onChange={e => setPv({ ...pv, favRestaurant: e.target.value })} placeholder="(blank = generic)" style={{ width: 150 }} /></label>
          <button onClick={runPreview} disabled={pvLoading || !aiConfigured} style={btn('#6366F1')}>{pvLoading ? 'Generating…' : 'Generate'}</button>
        </div>
        <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>Fill a fav dish/restaurant to preview personalized copy (uses {'{{favItem}}'} / {'{{favRestaurant}}'}); leave blank to see the generic version.</div>
        {pvResult && (
          <div style={{ marginTop: 14, background: '#F9FAFB', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12, color: '#888' }}>As the customer sees it:</div>
            <div style={{ fontWeight: 700, marginTop: 4 }}>{pvResult.personalized.title}</div>
            <div>{pvResult.personalized.body}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
              Template (with placeholder): “{pvResult.raw.title}” — “{pvResult.raw.body}”
              {pvResult.matchedRule && <> · matched rule: <b>{pvResult.matchedRule.name || 'unnamed'}</b></>}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...card, marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0 }}>🧪 A/B variant performance</h3>
          <button onClick={loadVariants} disabled={vLoading} style={{ ...btn('#6366F1'), padding: '6px 12px' }}>{vLoading ? 'Loading…' : 'Refresh'}</button>
        </div>
        <p style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
          The engine keeps up to a few AI copy variants per cohort and self-optimizes toward the best-converting one (order rate). Sends/conversions accrue as re-engagement runs; the winner gets shown more.
        </p>
        {variants && (variants.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13 }}>No variants yet — they're created the first time AI copy runs for a cohort (AI ON + re-engagement ON).</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee', color: '#555' }}>
                  <th style={{ padding: '6px 8px' }}>Cohort</th>
                  <th style={{ padding: '6px 8px' }}>Copy</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sends</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Orders</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {variants.map(v => (
                  <tr key={v._id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: '#777', whiteSpace: 'nowrap' }}>{v.cohortKey}</td>
                    <td style={{ padding: '6px 8px' }}><b>{v.title}</b><br /><span style={{ color: '#666' }}>{v.body}</span></td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{v.sends}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{v.conversions}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: v.rate >= 10 ? '#059669' : '#555' }}>{v.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========================== DAILY NUDGE TAB ==================================
function DailyNudgeTab({ onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [pv, setPv] = useState({ sampleName: 'Aarav', favItem: 'Paneer Butter Masala', favRestaurant: 'Curry Heaven' });
  const [pvResult, setPvResult] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);
  const [slots, setSlots] = useState(null);
  const [genreOptions, setGenreOptions] = useState(['warm', 'playful', 'urgent', 'foodie', 'minimal', 'bold']);
  const [imgUrl, setImgUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // test-send
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await dailyNudgeAPI.getSettings();
      const d = r.data.data || {};
      setAiConfigured(!!d.aiConfigured);
      setGenreOptions(d.genreOptions || ['warm', 'playful', 'urgent', 'foodie', 'minimal', 'bold']);
      setCfg({
        enabled: !!d.enabled,
        times: Array.isArray(d.times) ? d.times : [],
        windowMinutes: d.windowMinutes ?? 10,
        dailyCap: d.dailyCap ?? 5000,
        useAI: d.useAI !== false,
        excludeIfOrderedWithinHours: d.excludeIfOrderedWithinHours ?? 0,
        length: d.length || 'short',
        genre: d.genre || '',
        cravingImages: Array.isArray(d.cravingImages) ? d.cravingImages : [],
      });
    } catch (e) { setMsg('Failed to load settings'); }
  }, []);
  const loadStats = useCallback(async () => {
    try { const r = await dailyNudgeAPI.getStats(); setSlots(r.data.data.slots || []); }
    catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg('');
    try { await dailyNudgeAPI.updateSettings(cfg); setMsg('Saved.'); load(); onChanged && onChanged(); }
    catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const addTime = () => set('times', [...cfg.times, '20:00']);
  const setTime = (i, v) => set('times', cfg.times.map((t, idx) => (idx === i ? v : t)));
  const removeTime = (i) => set('times', cfg.times.filter((_, idx) => idx !== i));

  const addCravingImage = (url) => {
    const u = String(url || '').trim();
    if (!/^https?:\/\/.+/i.test(u)) { setMsg('Enter a valid https image URL.'); return; }
    if ((cfg.cravingImages || []).includes(u)) { setImgUrl(''); return; }
    set('cravingImages', [...(cfg.cravingImages || []), u].slice(0, 20));
    setImgUrl('');
  };
  const removeCravingImage = (i) => set('cravingImages', cfg.cravingImages.filter((_, idx) => idx !== i));
  const uploadCravingImage = async (file) => {
    if (!file) return;
    setUploading(true); setMsg('');
    try {
      const fd = new FormData(); fd.append('image', file);
      const res = await adminAPI.uploadNotificationImage(fd);
      const url = res.data?.url || res.data?.data?.url || res.data?.imageUrl;
      if (url) addCravingImage(url); else setMsg('Upload returned no URL.');
    } catch (e) { setMsg(e.response?.data?.message || 'Image upload failed'); }
    finally { setUploading(false); }
  };

  const runPreview = async () => {
    setPvLoading(true); setPvResult(null);
    try { const r = await dailyNudgeAPI.preview({ ...pv, useAI: cfg.useAI, length: cfg.length, genre: cfg.genre }); setPvResult(r.data.data); }
    catch (e) { setMsg(e.response?.data?.message || 'Preview failed'); }
    finally { setPvLoading(false); }
  };

  const runTestSend = async () => {
    setTesting(true); setTestResult(null); setMsg('');
    try { const r = await dailyNudgeAPI.testSend({ phone: testPhone }); setTestResult({ ok: true, ...r.data.data }); loadStats(); }
    catch (e) { setTestResult({ ok: false, message: e.response?.data?.message || 'Test send failed' }); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div style={{ padding: 16 }}>🔄 Loading…</div>;

  return (
    <div>
      <div style={{ ...card, borderColor: '#F59E0B', background: '#FFFBEB', color: '#92400E' }}>
        ⚠️ <b>This messages your whole customer base at each time.</b> Over-messaging is the #1 reason people mute/uninstall — keep it to 1–2 times/day at real meal times.
      </div>
      {!aiConfigured && cfg.useAI && (
        <div style={{ ...card, borderColor: '#F59E0B', background: '#FFFBEB', color: '#92400E' }}>
          ⚠️ ANTHROPIC_API_KEY not set — AI copy will fall back to templates.
        </div>
      )}
      {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669' }}>{msg}</div>}

      <div style={card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          <b>Daily nudge {cfg.enabled ? 'ON' : 'OFF'}</b>
          <span style={{ fontSize: 12, color: '#999' }}>{cfg.enabled ? 'Broadcasts at the times below.' : 'Off — nothing sends (you can still send a test below).'}</span>
        </label>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Send times <span style={{ fontSize: 12, color: '#999' }}>(IST — each fires once/day)</span></h3>
        {cfg.times.length === 0 && <div style={{ color: '#999', fontSize: 13, marginBottom: 8 }}>No times yet — add one (e.g. 13:00 lunch, 20:00 dinner).</div>}
        {cfg.times.map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <input type="time" value={t} onChange={(e) => setTime(i, e.target.value)} />
            <button onClick={() => removeTime(i)} style={{ ...btn('#EF4444'), padding: '4px 10px' }}>Remove</button>
          </div>
        ))}
        <button onClick={addTime} style={btn('#6366F1')}>+ Add time</button>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          <label>Fire window (min)<br />
            <input type="number" min="1" max="120" value={cfg.windowMinutes} onChange={(e) => set('windowMinutes', Number(e.target.value))} style={{ width: 80 }} />
          </label>
          <label>Max sends / slot<br />
            <input type="number" min="0" max="100000" value={cfg.dailyCap} onChange={(e) => set('dailyCap', Number(e.target.value))} style={{ width: 100 }} />
          </label>
          <label>Skip if ordered within (hrs, 0=off)<br />
            <input type="number" min="0" max="168" value={cfg.excludeIfOrderedWithinHours} onChange={(e) => set('excludeIfOrderedWithinHours', Number(e.target.value))} style={{ width: 100 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <input type="checkbox" checked={cfg.useAI} onChange={(e) => set('useAI', e.target.checked)} /> AI-personalized copy
          </label>
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>Length<br />
            <select value={cfg.length} onChange={(e) => set('length', e.target.value)}>
              <option value="short">Short (punchy)</option><option value="medium">Medium</option><option value="long">Long (descriptive)</option>
            </select>
          </label>
          <label>Genre / tone <span style={{ fontSize: 11, color: '#999' }}>(pick or type custom)</span><br />
            <input list="nudge-genres" value={cfg.genre} onChange={(e) => set('genre', e.target.value)} placeholder="e.g. foodie" style={{ width: 200 }} />
            <datalist id="nudge-genres">{genreOptions.map((g) => <option key={g} value={g} />)}</datalist>
          </label>
          <span style={{ fontSize: 11, color: '#999' }}>Own length/genre for the nudge (brand voice comes from the AI Copy tab).</span>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>🤤 Craving photos <span style={{ fontSize: 12, color: '#999' }}>(hunger-inducing food images attached to the push)</span></h3>
        <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
          Every nudge carries a mouth-watering food photo so people feel hungry and want to order. A customer's own
          favourite-dish photo is used when we have it; otherwise we attach <b>one of these at random</b>. Add a few
          appetizing, high-quality shots — <b>any shape is fine</b>: on save we auto-fit each onto a 2:1 frame (with a
          soft blurred background) so the <b>full photo</b> shows in the notification instead of being cropped. Landscape
          (~2:1, e.g. 1024×512) still looks best. Leave empty to only use favourite-dish photos.
        </p>
        {(cfg.cravingImages || []).length === 0 && (
          <div style={{ color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
            No craving photos yet — customers without a favourite dish will get a text-only push. Add at least one.
          </div>
        )}
        {(cfg.cravingImages || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            {cfg.cravingImages.map((u, i) => (
              <div key={u} style={{ position: 'relative', width: 150 }}>
                <img src={u} alt={`craving ${i + 1}`} style={{ width: 150, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                  onError={(e) => { e.target.style.opacity = 0.3; }} />
                <button onClick={() => removeCravingImage(i)} title="Remove"
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(239,68,68,0.95)', color: '#fff', border: 'none', borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: '1' }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="url" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="https://…/food.jpg" style={{ flex: '1 1 320px', minWidth: 240, padding: '6px 10px' }} />
          <button onClick={() => addCravingImage(imgUrl)} style={btn('#6366F1')}>+ Add URL</button>
          <label style={{ ...btn('#0EA5E9'), display: 'inline-block' }}>
            {uploading ? 'Uploading…' : '⬆ Upload'}
            <input type="file" accept="image/*" disabled={uploading} style={{ display: 'none' }}
              onChange={(e) => { uploadCravingImage(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <span style={{ fontSize: 12, color: '#999' }}>{(cfg.cravingImages || []).length}/20</span>
        </div>
      </div>

      <button onClick={save} disabled={saving} style={{ ...btn('#10B981'), fontSize: 15 }}>{saving ? 'Saving…' : '💾 Save nudge settings'}</button>

      {/* Test send */}
      <div style={{ ...card, marginTop: 24, borderColor: '#7c3aed' }}>
        <h3 style={{ marginTop: 0 }}>📲 Send a test nudge <span style={{ fontSize: 12, color: '#999' }}>(one real push to a single device — not the whole base)</span></h3>
        <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
          Sends the exact live nudge (AI copy + a random craving photo) to one customer's device, so you can see it on a phone. The number must have the customer app installed, logged in, with notifications allowed. <b>Save first</b> so it uses your latest photos/settings.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Customer phone (10 digits)" style={{ width: 240, padding: '6px 10px' }} />
          <button onClick={runTestSend} disabled={testing || testPhone.replace(/\D/g, '').length < 10} style={btn('#7c3aed')}>{testing ? 'Sending…' : 'Send test nudge'}</button>
        </div>
        {testResult && (
          testResult.ok ? (
            <div style={{ marginTop: 12, background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 13, color: testResult.sent > 0 ? '#059669' : '#B45309', fontWeight: 700 }}>
                {testResult.sent > 0 ? `✅ Sent to ${testResult.to} (${testResult.devices} device${testResult.devices > 1 ? 's' : ''})` : `⚠️ Delivered to 0 devices for ${testResult.to}`}
              </div>
              {testResult.image ? (
                <img src={testResult.image} alt="sent" style={{ width: '100%', maxWidth: 360, height: 160, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
              ) : null}
              <div style={{ fontWeight: 700, marginTop: 8 }}>{testResult.title}</div>
              <div>{testResult.body}</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                {testResult.ai ? 'AI copy' : 'template'} · photo: {testResult.imageIsFav ? "customer's favourite dish" : (testResult.image ? 'craving pool (random)' : 'none')}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 12, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 12, fontSize: 13 }}>{testResult.message}</div>
          )
        )}
      </div>

      {/* Preview */}
      <div style={{ ...card, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>🔍 Preview</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>Sample name<br /><input value={pv.sampleName} onChange={(e) => setPv({ ...pv, sampleName: e.target.value })} style={{ width: 110 }} /></label>
          <label>Fav dish<br /><input value={pv.favItem} onChange={(e) => setPv({ ...pv, favItem: e.target.value })} placeholder="(blank = generic)" style={{ width: 160 }} /></label>
          <label>Fav restaurant<br /><input value={pv.favRestaurant} onChange={(e) => setPv({ ...pv, favRestaurant: e.target.value })} placeholder="(blank = generic)" style={{ width: 160 }} /></label>
          <button onClick={runPreview} disabled={pvLoading} style={btn('#6366F1')}>{pvLoading ? 'Generating…' : 'Generate'}</button>
        </div>
        {pvResult && (
          <div style={{ marginTop: 14, background: '#F9FAFB', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12, color: '#888' }}>As the customer sees it{pvResult.ai ? ' (AI)' : ' (template)'}:</div>
            {pvResult.cravingImage ? (
              <img src={pvResult.cravingImage} alt="craving" style={{ width: '100%', maxWidth: 360, height: 160, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div style={{ fontSize: 11, color: '#B45309', marginTop: 6 }}>No craving photo set — add one above so the push carries a food image.</div>
            )}
            <div style={{ fontWeight: 700, marginTop: 8 }}>{pvResult.personalized.title}</div>
            <div>{pvResult.personalized.body}</div>
            {pvResult.cravingImageCount > 1 && (
              <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>Photo rotates at random across {pvResult.cravingImageCount} craving images (plus each customer's own favourite dish when available).</div>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0 }}>Recent sends</h3>
          <button onClick={loadStats} style={{ ...btn('#6366F1'), padding: '6px 12px' }}>Refresh</button>
        </div>
        {slots && (slots.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>No slots fired yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee', color: '#555' }}>
                <th style={{ padding: '6px 8px' }}>Slot (IST)</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Audience</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sent</th>
                <th style={{ padding: '6px 8px' }}>Copy</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => (
                <tr key={s.slotKey} style={{ borderBottom: '1px solid #f2f2f2' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{s.slotKey}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{s.audience}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{s.sent}</td>
                  <td style={{ padding: '6px 8px' }}>{s.usedAI ? 'AI' : 'template'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}

// ============================== HUB ==========================================
function MessagingHub() {
  const location = useLocation();
  const [tab, setTab] = useState(location.pathname.includes('daily-nudge') ? 'nudge' : 'ai');
  const [ov, setOv] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const [a, n, st] = await Promise.all([
        aiMessagingAPI.getSettings(),
        dailyNudgeAPI.getSettings(),
        dailyNudgeAPI.getStats().catch(() => null),
      ]);
      setOv({
        ai: a.data.data || {},
        nudge: n.data.data || {},
        lastSlot: (st && st.data && st.data.data && st.data.data.slots && st.data.data.slots[0]) || null,
      });
    } catch (e) { /* non-fatal — chips just stay blank */ }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const ctx = ov && ov.ai && ov.ai.currentContext;
  const nudge = (ov && ov.nudge) || {};
  const ai = (ov && ov.ai) || {};

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 4 }}>📣 Messaging Hub</h1>
        <p style={{ color: '#777', marginTop: 0 }}>AI-written push copy (re-engagement, per segment + time/weather) and the Daily Food Nudge broadcast — one place. They share the same brand voice & model.</p>

        {/* Status dashboard */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          <Chip label="AI copy" value={ai.enabled ? 'ON' : 'OFF'} color={ai.enabled ? '#059669' : '#9CA3AF'} sub={ai.aiConfigured ? 'API key set' : 'no API key'} />
          <Chip label="Daily nudge" value={nudge.enabled ? 'ON' : 'OFF'} color={nudge.enabled ? '#059669' : '#9CA3AF'} sub={nudge.enabled ? `at ${nextSendLabel(nudge.times)} IST` : 'not sending'} />
          <Chip label="Context now" value={ctx ? ctx.daypart : '—'} sub={ctx ? `${ctx.dayType} · ${ctx.weather}` : (ai.context ? `weather ${ai.context.mode}` : '')} />
          <Chip label="Craving photos" value={(nudge.cravingImages || []).length} color={(nudge.cravingImages || []).length ? '#7c3aed' : '#B45309'} sub={(nudge.cravingImages || []).length ? 'in pool' : 'add some'} />
          <Chip label="Last nudge" value={ov && ov.lastSlot ? `${ov.lastSlot.sent}/${ov.lastSlot.audience}` : '—'} sub={ov && ov.lastSlot ? ov.lastSlot.slotKey : 'none fired yet'} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[{ k: 'ai', l: '🤖 AI Copy & Context' }, { k: 'nudge', l: '🍽️ Daily Nudge' }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{ ...btn(tab === t.k ? '#FF6961' : '#eee'), color: tab === t.k ? '#fff' : '#333', fontWeight: tab === t.k ? 700 : 400 }}>{t.l}</button>
          ))}
        </div>

        {tab === 'ai' ? <AiCopyTab onChanged={loadOverview} /> : <DailyNudgeTab onChanged={loadOverview} />}
      </div>
    </div>
  );
}

export default MessagingHub;
